import { Socket } from 'Src/models/socket';
import { log as baseLogger } from 'Src/utils/log';

import * as types from './types';

const sectionId = 'NotificationProcessor';
const log = baseLogger.getSubLogger({ name: sectionId });

/**
 * Add more POS notification events here as we define them
 */
declare global {
  export interface GlobalEventHandlersEventMap {
    'notify.AuthorizationRequestAnswered': types.CustomNotificationEvent<types.AuthorizationRequestAnswered>;
    'notify.AuthorizationRequestCreated': types.CustomNotificationEvent<types.AuthorizationRequestCreated>;
    'notify.CheckCreated': types.CustomNotificationEvent<types.CheckHeaderCreated>;
    'notify.CheckPrintJobActionTaken': types.CustomNotificationEvent<types.CheckPrintJobActionTakenNotification>;
    'notify.CheckPrintJobResult': types.CustomNotificationEvent<types.CheckPrintJobResultNotification>;
    'notify.CheckUpdated': types.CustomNotificationEvent<types.CheckHeaderUpdated>;
    'notify.CheckPaymentGoodFaithRerun': types.CustomNotificationEvent<types.CheckPaymentGoodFaithRerun>;
    'notify.CheckPaymentGoodFaithTransaction': types.CustomNotificationEvent<types.CheckPaymentGoodFaithTransaction>;
    'notify.DebugLogsEngage': types.CustomNotificationEvent<types.DebugLogsEngage>;
    'notify.EntityCUD.Employee.Created': types.CustomNotificationEvent<types.Notification_EntityCUD_Employee_Created>;
    'notify.EntityCUD.Employee.Deleted': types.CustomNotificationEvent<types.Notification_EntityCUD_Employee_Created>;
    'notify.EntityCUD.Employee.Updated': types.CustomNotificationEvent<types.Notification_EntityCUD_Employee_Updated>;
    'notify.EntityCUD.EmployeeShift.Created': types.CustomNotificationEvent<types.Notification_EntityCUD_EmployeeShift_Created>;
    'notify.EntityCUD.EmployeeShift.Deleted': types.CustomNotificationEvent<types.Notification_EntityCUD_EmployeeShift_Deleted>;
    'notify.EntityCUD.EmployeeShift.Updated': types.CustomNotificationEvent<types.Notification_EntityCUD_EmployeeShift_Updated>;
    'notify.EntityCUD.Location.Created': types.CustomNotificationEvent<types.Notification_EntityCUD_Location_Created>;
    'notify.EntityCUD.Location.Deleted': types.CustomNotificationEvent<types.Notification_EntityCUD_Location_Deleted>;
    'notify.EntityCUD.Location.Updated': types.CustomNotificationEvent<types.Notification_EntityCUD_Location_Updated>;
    'notify.EntityCUD.OloConfig.Created': types.CustomNotificationEvent<types.Notification_EntityCUD_OloConfig_Created>;
    'notify.EntityCUD.OloConfig.Deleted': types.CustomNotificationEvent<types.Notification_EntityCUD_OloConfig_Deleted>;
    'notify.EntityCUD.OloConfig.Updated': types.CustomNotificationEvent<types.Notification_EntityCUD_OloConfig_Updated>;
    'notify.EntityCUD.Table.Created': types.CustomNotificationEvent<types.Notification_EntityCUD_Table_Created>;
    'notify.EntityCUD.Table.Deleted': types.CustomNotificationEvent<types.Notification_EntityCUD_Table_Deleted>;
    'notify.EntityCUD.Table.Updated': types.CustomNotificationEvent<types.Notification_EntityCUD_Table_Updated>;
    'notify.EODTaskCompleted': types.CustomNotificationEvent<types.EODTaskCompleted>;
    'notify.EODTaskFailed': types.CustomNotificationEvent<types.EODTaskFailed>;
    'notify.FloorplanUpserted': types.CustomNotificationEvent<types.FloorplanUpserted>;
    'notify.PrinterInfoChanged': types.CustomNotificationEvent<types.PrinterInfoChanged>;
    'notify.QuantityUpdate': types.CustomNotificationEvent<types.QuantityUpdate>;
    'notify.S2P_CheckPaid': types.CustomNotificationEvent<types.S2PCheckPaid>;
    'notify.TerminalReleaseNotification': types.CustomNotificationEvent<types.TerminalRelease>;
    'notify.TerminalLANPrintJob': types.CustomNotificationEvent<types.TerminalLANPrintJob>;
  }
}

/**
 * NotificationProcessor receives generic socket notifications from the server
 * and relays them out as typed notifications that have specific
 * purpose or meaning for the app via DOM events.
 */
class NotificationProcessor {
  constructor(s: Socket) {
    s.on('notification', (n) => {
      if (!this.handleEvent(n as types.Notifications)) {
        log.debug('received notification from server, but it is of unknown type: {@n}', n);
      }
    });
  }

  /**
   * Parse out the type of notification and emit/broadcast
   * @param n the generic notification
   * @returns true if it was emitted, false if we can't figure out what it is
   */
  handleEvent(n: types.Notifications): boolean {
    let evtType: keyof GlobalEventHandlersEventMap | null = null;
    const evtData = n.data;

    switch (n.type) {
      case 'AuthorizationRequestAnswered': evtType = 'notify.AuthorizationRequestAnswered'; break;
      case 'AuthorizationRequestCreated': evtType = 'notify.AuthorizationRequestCreated'; break;
      case 'CheckCreated': evtType = 'notify.CheckCreated'; break;
      case 'CheckPaymentGoodFaithRerun': evtType = 'notify.CheckPaymentGoodFaithRerun'; break;
      case 'CheckPaymentGoodFaithTransaction': evtType = 'notify.CheckPaymentGoodFaithTransaction'; break;
      case 'CheckPrintJobActionTaken': evtType = 'notify.CheckPrintJobActionTaken'; break;
      case 'CheckPrintJobResult': evtType = 'notify.CheckPrintJobResult'; break;
      case 'CheckUpdated': evtType = 'notify.CheckUpdated'; break;
      case 'DebugLogsEngage': evtType = 'notify.DebugLogsEngage'; break;
      case 'FloorplanUpserted': evtType = 'notify.FloorplanUpserted'; break;
      case 'PrinterInfoConfigChange': evtType = 'notify.PrinterInfoChanged'; break;
      case 'TerminalLANPrintJob': evtType = 'notify.TerminalLANPrintJob'; break;
      case 'TerminalReleaseNotification': evtType = 'notify.TerminalReleaseNotification'; break;
      case 'EODTaskFailed': evtType = 'notify.EODTaskFailed'; break;
      case 'EODTaskCompleted': evtType = 'notify.EODTaskCompleted'; break;
      case 'QuantityUpdate': evtType = 'notify.QuantityUpdate'; break;
      case 'S2P_CheckPaid': evtType = 'notify.S2P_CheckPaid'; break;
      case 'EntityCUD': {
        evtType = `notify.EntityCUD.${n.data.type}.${n.data.action}`;
      }
    }
    if (evtType) {
      log.info('handleEvent()', evtType);
      document.dispatchEvent(new CustomEvent(evtType, { detail: evtData }));
      return true;
    }
    return false;
  }
}

export { NotificationProcessor };
